// src/admin/components/reports/widgets/OverdueTenantsWidget.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  RiUserLine,
  RiAlertLine,
  RiTimeLine,
  RiBuildingLine,
  RiMoneyDollarCircleLine
} from 'react-icons/ri';
import { getTenantPaymentStatus, formatCurrency } from '../../../../models/reportModel';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n';

const OverdueTenantsWidget = ({ isDarkMode }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const isRTL = i18n.language === 'ar' || i18n.language === 'ku';

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await getTenantPaymentStatus();
        setData(response);
      } catch (err) {
        console.error(t('error_fetching_tenant_status'), err);
        setError(err.message || t('failed_load_tenant_status'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [t]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-t-2 border-rose-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-rose-900/20 border-rose-800 text-rose-400' : 'bg-rose-50 border-rose-200 text-rose-700'
        } border flex items-center h-64 justify-center`}>
        <RiAlertLine className={`${isRTL ? 'ml-2' : 'mr-2'} text-xl`} />
        {error}
      </div>
    );
  }

  if (!data) return null;

  const overdueTenants = data.tenantPayments.filter(tenant => tenant.rentStatus === 'overdue');

  if (overdueTenants.length === 0) {
    return (
      <div className={`text-center p-8 ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
        {t('no_overdue_payments')}
      </div>
    );
  }

  // Show only first 5 overdue tenants
  const displayTenants = overdueTenants.slice(0, 5);

  return (
    <div style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-gray-50'} text-center`}>
          <div className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {data.summary.overdueCount}
          </div>
          <div className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
            {t('overdue')}
          </div>
        </div>

        <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-gray-50'} text-center`}>
          <div className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {data.summary.pendingCount}
          </div>
          <div className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
            {t('pending')}
          </div>
        </div>

        <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-gray-50'} text-center`}>
          <div className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {data.summary.paidCount}
          </div>
          <div className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
            {t('paid')}
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {displayTenants.map(tenant => (
          <div
            key={tenant.tenantId}
            className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800 hover:bg-gray-650' : 'bg-gray-50 hover:bg-gray-100'
              } transition-colors duration-200`}
          >
            <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={`h-10 w-10 rounded-full bg-zinc-500 flex items-center justify-center ${isRTL ? 'ml-3' : 'mr-3'}`}>
                  <RiUserLine className="text-white" />
                </div>

                <div className={isRTL ? 'text-right' : 'text-left'}>
                  <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {tenant.tenantName}
                  </p>
                  <div className={`flex items-center text-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <RiBuildingLine className={`${isRTL ? 'ml-1' : 'mr-1'} ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`} size={14} />
                    <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {tenant.buildingName}, {t('apt')} {tenant.apartmentNumber}
                    </span>
                  </div>
                </div>
              </div>

              <div className={isRTL ? 'text-left' : 'text-right'}>
                <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {formatCurrency(tenant.balance > 0 ? tenant.balance : tenant.rentAfterDiscount)}
                </div>
                <div className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-gray-500'} ${isRTL ? 'text-left' : 'text-right'} mt-0.5`}>
                  {t('paid_until')}: {new Date(tenant.nextBillingDate).toLocaleDateString(i18n.language === 'ar' ? 'ar-EG' : undefined)}
                </div>
                <div className={`flex items-center text-xs mt-1 ${isRTL ? 'flex-row-reverse justify-start' : 'justify-end'}`}>
                  <RiTimeLine className={`${isRTL ? 'ml-1' : 'mr-1'} text-rose-500`} size={12} />
                  <span className="text-rose-500">
                    {tenant.daysSinceLastPayment} {t('days_overdue')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}

        {overdueTenants.length > 5 && (
          <div className={`text-center text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
            {overdueTenants.length - 5} {t('more_overdue_tenants')}
          </div>
        )}
      </div>
    </div>
  );
};

export default OverdueTenantsWidget;